import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { FiPlus, FiSearch, FiEdit2, FiTrash2, FiEye, FiPhone } from 'react-icons/fi';

export default function Customers() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/customers', { params: { search, filter, page, limit: 20 } });
      setCustomers(res.data.customers);
      setTotal(res.data.total);
      setPages(res.data.pages);
    } catch (err) {
      toast.error('Failed to load customers');
    }
    setLoading(false);
  }, [search, filter, page]);

  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete customer "${name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/customers/${id}`);
      toast.success('Customer deleted');
      fetchCustomers();
    } catch { toast.error('Delete failed'); }
  };

  const getStatus = (customer) => {
    const hasPending = customer.measurements?.some(m => m.isPending);
    const allDelivered = customer.measurements?.every(m => m.isDelivered);
    if (allDelivered) return <span className="badge badge-success">Delivered</span>;
    if (hasPending) return <span className="badge badge-warning">Pending</span>;
    return <span className="badge badge-gray">No Orders</span>;
  };

  const getTotalAmount = (customer) => {
    return customer.measurements?.reduce((sum, m) => sum + (m.price || 0), 0) || 0;
  };

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20, flexWrap:'wrap', gap:12 }}>
        <div className="filter-tabs">
          {['all','pending','delivered'].map(f => (
            <button key={f} className={`filter-tab ${filter === f ? 'active' : ''}`}
              onClick={() => { setFilter(f); setPage(1); }}>
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
        <Link to="/customers/add" className="btn btn-accent">
          <FiPlus size={15} /> Add Customer
        </Link>
      </div>

      <div className="card">
        <div className="card-header">
          <span style={{ color:'#64748b', fontSize:13 }}>{total} customers</span>
          <div className="search-input-wrap" style={{ width:240 }}>
            <FiSearch size={14} />
            <input className="form-control" placeholder="Search name or phone..."
              value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
          </div>
        </div>

        {loading ? <div className="loading"><div className="spinner"></div></div> : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Phone</th>
                  <th>City</th>
                  <th>Delivery</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {customers.length === 0 && (
                  <tr><td colSpan={7}>
                    <div className="empty-state">
                      <div className="empty-state-icon">👕</div>
                      <h3>No customers found</h3>
                      <p>Add your first customer to get started</p>
                    </div>
                  </td></tr>
                )}
                {customers.map(c => (
                  <tr key={c._id}>
                    <td>
                      <div style={{ fontWeight:600 }}>{c.name}</div>
                      {c.measurements?.length > 0 && (
                        <div style={{ fontSize:11, color:'#94a3b8' }}>{c.measurements.length} order(s)</div>
                      )}
                    </td>
                    <td>
                      <div style={{ display:'flex', alignItems:'center', gap:4 }}>
                        <FiPhone size={12} color="#94a3b8" />
                        {c.phone}
                      </div>
                    </td>
                    <td>{c.city || '-'}</td>
                    <td>
                      {c.measurements?.[0]?.deliveryDate
                        ? new Date(c.measurements[0].deliveryDate).toLocaleDateString('en-AE', { day:'numeric', month:'short' })
                        : '-'}
                    </td>
                    <td>
                      <strong>SAR {getTotalAmount(c).toFixed(0)}</strong>
                      {c.measurements?.some(m => m.remaining > 0) && (
                        <div style={{ fontSize:11, color:'#ef4444' }}>
                          Due: SAR {c.measurements.reduce((s, m) => s + (m.remaining || 0), 0).toFixed(0)}
                        </div>
                      )}
                    </td>
                    <td>{getStatus(c)}</td>
                    <td>
                      <div style={{ display:'flex', gap:4 }}>
                        <button className="btn btn-outline btn-sm btn-icon" title="View"
                          onClick={() => navigate(`/customers/${c._id}`)}>
                          <FiEye size={14} />
                        </button>
                        <button className="btn btn-outline btn-sm btn-icon" title="Edit"
                          onClick={() => navigate(`/customers/${c._id}/edit`)}>
                          <FiEdit2 size={14} />
                        </button>
                        <button className="btn btn-outline btn-sm btn-icon" title="Delete"
                          style={{ color:'#ef4444', borderColor:'#fecaca' }}
                          onClick={() => handleDelete(c._id, c.name)}>
                          <FiTrash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {pages > 1 && (
          <div style={{ padding:'12px 16px' }}>
            <div className="pagination">
              {Array.from({ length: pages }, (_, i) => (
                <button key={i} className={`page-btn ${page === i+1 ? 'active' : ''}`}
                  onClick={() => setPage(i+1)}>{i+1}</button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}